import type { ClipBoxState } from '@/atoms/viewer.atom';
import * as THREE from 'three';

export interface ClipBoxPlanes {
  /** Six planes, two per box axis. */
  planes: THREE.Plane[];
  /**
   * Value for `Material.clipIntersection`. `false` for CLIP_OUTSIDE (a pixel
   * is discarded when it's outside ANY plane), `true` for CLIP_INSIDE (planes
   * are flipped; a pixel is discarded only when it's inside ALL of them).
   */
  clipIntersection: boolean;
}

const _q = new THREE.Quaternion();
const _c = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _n = new THREE.Vector3();

/**
 * Whether the clip-box state calls for Three.js material clipping at all.
 * HIGHLIGHT_INSIDE and NONE don't clip — they're handled by dimming/overlays.
 */
export function clipBoxShouldClip(state: ClipBoxState): boolean {
  return (
    state.enabled && state.center !== null && (state.clipMode === 'CLIP_OUTSIDE' || state.clipMode === 'CLIP_INSIDE')
  );
}

/**
 * Whether the clip-box state calls for the "dim everything, highlight the
 * box" presentation used by mesh layers (IFC, GLB).
 */
export function clipBoxIsHighlight(state: ClipBoxState): boolean {
  return state.enabled && state.center !== null && state.clipMode === 'HIGHLIGHT_INSIDE';
}

/**
 * Build the six Three.js clipping planes for an oriented clip box.
 *
 * Three.js keeps fragments where `dot(p, normal) + constant >= 0`, so each
 * plane's normal points *into* the box. For CLIP_INSIDE every plane is
 * negated and the caller must set `clipIntersection = true`.
 *
 * Pass `out` (an array that will be grown/truncated to 6 planes) to reuse
 * allocations across frames. Returns `null` when the state doesn't clip.
 */
export function buildClipBoxPlanes(state: ClipBoxState, out: THREE.Plane[] = []): ClipBoxPlanes | null {
  if (!clipBoxShouldClip(state)) return null;
  const { center, halfSize, rotation: rot } = state;

  _q.set(rot[0], rot[1], rot[2], rot[3]);
  _c.set(center![0], center![1], center![2]);

  while (out.length < 6) out.push(new THREE.Plane());
  out.length = 6;

  const isClipInside = state.clipMode === 'CLIP_INSIDE';
  const unit: [number, number, number][] = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  for (let i = 0; i < 3; i++) {
    _axis.set(unit[i][0], unit[i][1], unit[i][2]).applyQuaternion(_q);
    const d = _c.dot(_axis); // projection of center onto this axis
    const h = halfSize[i];
    // Positive face: inward normal = -axis, keeps dot(p, axis) <= d + h
    out[i * 2].set(_n.copy(_axis).negate(), d + h);
    // Negative face: inward normal = +axis, keeps dot(p, axis) >= d - h
    out[i * 2 + 1].set(_axis, -d + h);
    if (isClipInside) {
      out[i * 2].negate();
      out[i * 2 + 1].negate();
    }
  }

  return { planes: out, clipIntersection: isClipInside };
}

/**
 * CPU twin of the shader test for one world-space point: whether `clip` keeps
 * it. For things the planes can't reach (troika labels, marker groups) so they
 * vanish with the geometry they annotate. `null` keeps everything.
 */
export function clipBoxKeepsPoint(clip: ClipBoxPlanes | null, p: THREE.Vector3): boolean {
  if (!clip) return true;
  return clip.clipIntersection
    ? clip.planes.some((pl) => pl.distanceToPoint(p) >= 0)
    : clip.planes.every((pl) => pl.distanceToPoint(p) >= 0);
}

function eachMaterial(root: THREE.Object3D, fn: (mat: THREE.Material) => void) {
  root.traverse((child) => {
    if (!('material' in child)) return;
    const m = (child as THREE.Mesh).material;
    const mats = Array.isArray(m) ? m : [m];
    for (const mat of mats) if (mat) fn(mat);
  });
}

/**
 * Assign (or clear, when `clip` is null) clipping planes on one material.
 * Idempotent; `needsUpdate` is only flagged when the material's clipping
 * configuration actually changes, since it forces a shader rebuild.
 */
export function applyClipBoxPlanesToMaterial(mat: THREE.Material, clip: ClipBoxPlanes | null) {
  if (clip) {
    const changed = mat.clippingPlanes !== clip.planes || mat.clipIntersection !== clip.clipIntersection;
    mat.clippingPlanes = clip.planes;
    mat.clipIntersection = clip.clipIntersection;
    if (changed) mat.needsUpdate = true;
  } else if (mat.clippingPlanes) {
    mat.clippingPlanes = null;
    mat.clipIntersection = false;
    mat.needsUpdate = true;
  }
}

/** `applyClipBoxPlanesToMaterial` for every material under `root`. */
export function applyClipBoxPlanes(root: THREE.Object3D, clip: ClipBoxPlanes | null) {
  eachMaterial(root, (mat) => applyClipBoxPlanesToMaterial(mat, clip));
}

/**
 * Opacity multiplier mesh layers (IFC, GLB, LandXML) apply to themselves
 * while the clip box is in HIGHLIGHT_INSIDE mode, so the orange box overlay
 * stands out.
 */
export const CLIP_BOX_HIGHLIGHT_DIM = 0.25;
